<template>
  <div>
    <div class="d-flex align-center ga-3 pb-4">
      <SoTextField
        v-model="search"
        placeholder="Search templates"
        append-icon="search"
        hide-details
        clearable
        :style="{ width: '320px', maxWidth: '100%' }"
        data-testid="email-templates-search"
      />
      <SoSelect
        v-model="senderFilter"
        type="simple"
        :items="senderItems"
        :style="{ width: '200px' }"
        data-testid="email-templates-sender-filter"
      />
    </div>
    <SoDataTable
      :headers="headers"
      :items="filteredRows"
      :loading="query.isFetching || externalQuery.isFetching"
      :searchable="false"
      data-testid="email-templates-table"
    >
      <template #item.sender="{ internalItem }">
        <SoChip
          :type="senderChipType(internalItem.raw.sender)"
          :text="internalItem.raw.sender"
          size="small"
        />
      </template>
      <template #item.versionLabel="{ internalItem }">
        <div class="d-flex align-center ga-2">
          <SoStatus :status="liveStatus(internalItem.raw)" />
          <SoChip
            v-if="
              internalItem.raw.liveVersion !== internalItem.raw.latestVersion
            "
            :type="SoChipType.IMPORTANT"
            :text="`v${internalItem.raw.latestVersion} draft`"
            size="small"
          />
        </div>
      </template>
      <template #itemActions="{ internalItem }">
        <div class="d-flex justify-end ga-1">
          <SoButton
            v-if="mandrillUrl(internalItem.raw)"
            title="Mandrill"
            type="table-action"
            icon="external-link sm"
            :href="mandrillUrl(internalItem.raw) ?? undefined"
            target="_blank"
            data-testid="mandrill-activity"
            @click.stop
          />
          <SoButton
            title="Show"
            type="table-action"
            data-testid="show-template"
            @click.stop="onRowClick(internalItem.raw)"
          />
        </div>
      </template>
    </SoDataTable>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { RouteNames } from "@/types/router"
  import { EmailTemplatesService } from "@/services/admin/EmailTemplatesService"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import SoStatus from "@/components/SoStatus.vue"
  import { IStatus } from "@/types/Status"
  import { SoSelectItem } from "@/types/components/SoSelect"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { EmailTemplateSender } from "@/models/admin/emailTemplates/emailTemplates.types"
  import {
    CommunicationRow,
    buildExternalRows,
    filterRows,
    groupTemplatesByCommunication,
    mergeRows,
  } from "../helpers/emailTemplateRows"
  import { mandrillActivityUrl } from "../helpers/mandrillLink"
  import { senderChipType } from "../helpers/senderPresentation"

  const headers: SoDataTableHeader[] = [
    { title: "Communication Channel", key: "communicationName" },
    { title: "Sender", key: "sender" },
    {
      title: "Live Subject",
      key: "liveSubject",
      fallbackValue: "No live version",
    },
    { title: "Modified By", key: "liveModifiedBy", fallbackValue: "—" },
    {
      title: "Published At",
      key: "livePublishedAt",
      type: SoDataTableColumnType.DATE_TIME,
      fallbackValue: "—",
    },
    { title: "Live Version", key: "versionLabel" },
  ]

  export default defineComponent({
    components: {
      SoDataTable,
      SoTextField,
      SoSelect,
      SoButton,
      SoChip,
      SoStatus,
    },
    data() {
      return {
        RouteNames,
        SoChipType,
        headers,
        search: "",
        senderFilter: null as EmailTemplateSender | null,
        query: EmailTemplatesService.getEmailTemplateSummaries(),
        externalQuery: EmailTemplatesService.getExternalEmailTemplates(),
      }
    },
    computed: {
      rows(): CommunicationRow[] {
        return mergeRows(
          groupTemplatesByCommunication(this.query.data ?? []),
          buildExternalRows(this.externalQuery.data ?? []),
        )
      },
      senderItems(): SoSelectItem[] {
        const senders = [...new Set(this.rows.map((row) => row.sender))].sort()
        return [
          { title: "All senders", value: null },
          ...senders.map((sender) => ({ title: sender, value: sender })),
        ]
      },
      filteredRows(): (CommunicationRow & SoDataTableItem)[] {
        return filterRows(
          this.rows,
          this.search,
          this.senderFilter,
        ) as (CommunicationRow & SoDataTableItem)[]
      },
    },
    methods: {
      senderChipType,
      liveStatus(row: CommunicationRow): IStatus {
        return row.liveVersion === null
          ? { type: "default", text: "No live version" }
          : { type: "normal", text: `v${row.liveVersion} live` }
      },
      mandrillUrl(row: CommunicationRow): string | null {
        return mandrillActivityUrl(row.liveSubject)
      },
      onRowClick(row: CommunicationRow) {
        if (row.readOnly) {
          this.$router.push({
            name: RouteNames.AdminExternalEmailTemplateDetail,
            params: { id: String(row.externalId) },
          })
          return
        }
        this.$router.push({
          name: RouteNames.AdminEmailTemplatesDetail,
          params: { communicationId: String(row.communicationId) },
        })
      },
    },
  })
</script>
